import { useId, useMemo, useState } from 'react'
import {
  TextInput, Select, CheckboxDropdown, Checkbox, RadioGroup, Textarea, DatePicker, InfoHint, Tag,
  type DateValue,
} from '@datahub/design-system'
import type { FieldDef, Taxonomy } from '@datahub/shared'
import { taxonomies, isPresent, hasRowOptions, tidyUrl } from '@datahub/shared'
import { useDraftContext } from './draft-context'
import { rowSelectOptions, currencyAriaLabel, emptyRowsHint, clearableValue, rowSelectValue, rowMultiValue } from './row-select'

export interface FieldRendererProps {
  field: FieldDef
  value: unknown
  onChange: (value: unknown) => void
  error?: string
  disabled?: boolean
  /** Values of the sibling fields, used for taxonomy filtering (sub-sector by sector). */
  siblings?: Record<string, unknown>
}

function optionsFor(field: FieldDef, siblings?: Record<string, unknown>): { value: string; label: string }[] {
  let options = field.options ?? []
  if (field.filterBy && field.taxonomy) {
    const parent = siblings?.[field.filterBy]
    const parents = Array.isArray(parent) ? parent.map(String) : parent ? [String(parent)] : []
    if (parents.length > 0) {
      const list = (taxonomies[field.taxonomy] ?? []) as Taxonomy[]
      const filtered = list.filter((t) => parents.includes(String(t['sector'] ?? '')))
      if (filtered.length > 0) options = filtered.map((t) => ({ value: t.slug, label: t.name }))
    }
  }
  return options
}

function Label({ field }: { field: FieldDef }) {
  if (!field.help) return <>{field.label}</>
  return (
    <span className="inline-flex items-center" style={{ gap: 4 }}>
      {field.label}
      <InfoHint content={field.help} />
    </span>
  )
}

/** Plain-text rendering for a `readOnlyExisting` field once it already carries a value. */
export function readOnlyText(field: FieldDef, value: unknown): string {
  if (field.input === 'taglist' && Array.isArray(value)) return (value as string[]).join(', ')
  if (field.input === 'checkbox') return value ? 'Yes' : 'No'
  return value === null || value === undefined ? '' : String(value)
}

/**
 * A `readOnlyExisting` field only turns read-only once it already holds a value — that
 * is what "existing" means (spec 5.1.3). An unset one is still the normal, editable input.
 */
export function isReadOnlyExisting(field: FieldDef, value: unknown): boolean {
  return Boolean(field.readOnlyExisting) && isPresent(value)
}

/**
 * `DatePicker` always reports `{ value, precision }`. The dictionary only asks for that
 * shape when `field.precision` is set — `packages/shared`'s `schemaForField` expects a
 * bare `YYYY-MM-DD` string otherwise — so a non-precision field keeps only the string
 * half of what it emits.
 */
export function dateOnChangeValue(field: FieldDef, v: DateValue): DateValue | string {
  return field.precision ? v : v.value
}

function ReadOnlyField({ field, value }: { field: FieldDef; value: unknown }) {
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        <Label field={field} />
      </span>
      <span className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px' }}>
        {readOnlyText(field, value)}
      </span>
    </div>
  )
}

/** Money value: amount plus its own currency, or explicitly undisclosed (spec 7.2). */
function MoneyField({ field, value, onChange, error, disabled }: FieldRendererProps) {
  const money = (value ?? {}) as { amount?: number | null; currency?: string; undisclosed?: boolean }
  // The ISO 4217 code alone. Spelling the currency out ran past the width of the control
  // beside the amount, and the code is what Stears uses everywhere else anyway.
  const currencies = (taxonomies['currencies'] ?? []).map((c) => ({ value: c.slug, label: c.slug }))

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        <Label field={field} />
      </span>
      <div className="flex items-start" style={{ gap: 10 }}>
        <div style={{ width: 100 }}>
          <Select
            size="large"
            ariaLabel={currencyAriaLabel(field.label, money.currency, 'money')}
            options={currencies}
            value={money.currency ?? ''}
            onChange={(currency) => onChange({ ...money, currency })}
            placeholder="Currency"
            disabled={disabled || money.undisclosed}
          />
        </div>
        <div style={{ flex: 1 }}>
          <TextInput
            size="large"
            ariaLabel={`${field.label} amount`}
            value={money.amount === null || money.amount === undefined ? '' : String(money.amount)}
            onChange={(v) => onChange({ ...money, amount: v === '' ? null : Number(v.replace(/[^0-9.-]/g, '')) })}
            placeholder="Amount in full units"
            disabled={disabled || money.undisclosed}
            {...(error ? { error } : {})}
          />
        </div>
        {field.undisclosed && (
          <div style={{ paddingTop: 12 }}>
            <Checkbox
              checked={Boolean(money.undisclosed)}
              onChange={(undisclosed) => onChange(undisclosed ? { undisclosed: true } : { amount: null })}
              label="Undisclosed"
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  )
}

/** Repeatable free-text list, for brand names or participating funds. */
function TagListField({ field, value, onChange, disabled }: FieldRendererProps) {
  const [entry, setEntry] = useState('')
  const items = Array.isArray(value) ? (value as string[]) : []
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        <Label field={field} />
      </span>
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        {items.map((item, i) => (
          <Tag key={`${item}-${i}`} onDismiss={disabled ? undefined : () => onChange(items.filter((_, j) => j !== i))}>
            {item}
          </Tag>
        ))}
      </div>
      <div className="flex items-start" style={{ gap: 10 }}>
        <div style={{ flex: 1 }}>
          <TextInput size="large" ariaLabel={field.label} value={entry} onChange={setEntry} placeholder="Type a name, then Add" disabled={disabled} />
        </div>
        <button
          type="button"
          disabled={disabled || entry.trim() === ''}
          onClick={() => { onChange([...items, entry.trim()]); setEntry('') }}
          className="font-jakarta text-white"
          style={{
            height: 40, padding: '0 16px', fontSize: 12, cursor: 'pointer',
            background: '#4B5563', border: 'none', opacity: entry.trim() === '' ? 0.6 : 1,
          }}
        >
          Add
        </button>
      </div>
    </div>
  )
}

/** Investors, buyers or lenders: a name plus the role they played. */
function PartyListField({ field, value, onChange, error, disabled }: FieldRendererProps) {
  const parties = Array.isArray(value) ? (value as { name: string; role?: string }[]) : []
  const roles = (field.roles ?? []).map((r) => ({ value: r, label: r }))

  const update = (index: number, next: Partial<{ name: string; role: string }>) => {
    onChange(parties.map((p, i) => (i === index ? { ...p, ...next } : p)))
  }

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        <Label field={field} />
      </span>
      {parties.map((party, i) => (
        <div key={i} className="flex items-start" style={{ gap: 10 }}>
          <div style={{ flex: 1 }}>
            <TextInput size="large" value={party.name} onChange={(name) => update(i, { name })} placeholder="Organisation name" disabled={disabled} />
          </div>
          <div style={{ width: 180 }}>
            <Select size="large" ariaLabel={`${field.label} party ${i + 1} role`} options={roles} value={party.role ?? ''} onChange={(role) => update(i, { role })} placeholder="Role" disabled={disabled} />
          </div>
          <button
            type="button"
            onClick={() => onChange(parties.filter((_, j) => j !== i))}
            className="font-jakarta text-text-support hover:text-white"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, height: 40 }}
            disabled={disabled}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...parties, { name: '' }])}
        className="font-jakarta text-white self-start"
        style={{ background: '#1A1D21', border: '0.5px solid #4B5563', padding: '0 12px', height: 32, fontSize: 12, cursor: 'pointer' }}
        disabled={disabled}
      >
        Add party
      </button>
      {error && <span className="font-mulish" style={{ fontSize: 10, color: '#F05042' }}>{error}</span>}
    </div>
  )
}

/** A select or multiselect whose choices are the values of a field on the draft's rows (`optionsFromRows`). */
function RowOptionsField({ field, value, onChange, error, disabled }: FieldRendererProps) {
  const { payload, tabs } = useDraftContext()
  const hintId = useId()
  const source = field.optionsFromRows!
  const multi = field.input === 'multiselect'
  const clearable = !multi && field.required !== 'always'
  const { options, empty } = useMemo(
    () => rowSelectOptions(source, payload, value, { clearable }),
    [source, payload, value, clearable],
  )
  const sourceTab = tabs.find((t) => t.key === source.tab)
  const hint = emptyRowsHint(sourceTab?.shortLabel ?? sourceTab?.label ?? 'Funds')
  const nothingToChoose = empty && options.length === 0
  const describedBy = empty ? { ariaDescribedBy: hintId } : {}
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        <Label field={field} />
      </span>
      {multi ? (
        <CheckboxDropdown
          size="large"
          ariaLabel={field.label}
          {...describedBy}
          options={options}
          value={rowMultiValue(value)}
          onChange={(choices) => onChange(rowMultiValue(choices))}
          placeholder="Choose all that apply"
          {...(error ? { error } : {})}
          disabled={disabled || nothingToChoose}
        />
      ) : (
        <Select
          size="large"
          ariaLabel={field.label}
          {...describedBy}
          options={options}
          value={rowSelectValue(value)}
          onChange={(choice) => onChange(clearableValue(choice))}
          {...(error ? { error } : {})}
          disabled={disabled || nothingToChoose}
        />
      )}
      {empty && (
        <span id={hintId} className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '12px' }}>
          {hint}
        </span>
      )}
    </div>
  )
}

/**
 * A web address, tidied once the answer is finished: someone typing `aruwacapital.com`
 * meant https://aruwacapital.com, so the portal completes it rather than refusing it.
 * Storing one shape is what keeps the address reading the same everywhere it is shown.
 */
function UrlField({ field, value, onChange, error, disabled }: FieldRendererProps) {
  const text = typeof value === 'string' ? value : ''
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        <Label field={field} />
      </span>
      <TextInput
        size="large"
        ariaLabel={field.label}
        value={text}
        onChange={onChange}
        onBlur={() => {
          const tidied = tidyUrl(text)
          if (tidied !== text) onChange(tidied)
        }}
        placeholder="aruwacapital.com"
        {...(error ? { error } : {})}
        disabled={disabled}
      />
    </div>
  )
}

/** Maps a dictionary field to its design-system control (plan Section 12.6). */
export function FieldRenderer(props: FieldRendererProps) {
  const { field, value, onChange, error, disabled, siblings } = props
  const options = useMemo(() => optionsFor(field, siblings), [field, siblings])
  const label = <Label field={field} />

  if (isReadOnlyExisting(field, value)) {
    return <ReadOnlyField field={field} value={value} />
  }

  if (hasRowOptions(field) && (field.input === 'select' || field.input === 'multiselect')) {
    return <RowOptionsField {...props} />
  }

  switch (field.input) {
    case 'longtext':
      return (
        <Textarea
          label={field.label}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          {...(error ? { error } : {})}
          {...(field.help ? { help: field.help } : {})}
          {...(field.maxLength ? { maxLength: field.maxLength } : {})}
          disabled={disabled}
        />
      )
    case 'select':
      return (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>{label}</span>
          <Select size="large" ariaLabel={field.taxonomy === 'currencies' ? currencyAriaLabel(field.label, typeof value === 'string' ? value : '', 'select') : field.label} options={options} value={typeof value === 'string' ? value : ''} onChange={onChange} {...(error ? { error } : {})} disabled={disabled} />
        </div>
      )
    case 'multiselect':
      return (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>{label}</span>
          <CheckboxDropdown
            size="large"
            ariaLabel={field.label}
            options={options}
            value={Array.isArray(value) ? (value as string[]) : []}
            onChange={onChange}
            placeholder="Choose all that apply"
            {...(error ? { error } : {})}
            disabled={disabled}
          />
        </div>
      )
    case 'radio':
      return (
        <RadioGroup
          label={field.label}
          options={options}
          value={typeof value === 'string' ? value : undefined}
          onChange={onChange}
          direction={options.length <= 3 ? 'row' : 'column'}
          {...(error ? { error } : {})}
          disabled={disabled}
        />
      )
    case 'checkbox':
      return <Checkbox checked={Boolean(value)} onChange={onChange} label={field.label} disabled={disabled} />
    case 'date':
      return (
        <DatePicker
          label={field.label}
          value={value as never}
          onChange={(v: DateValue) => onChange(dateOnChangeValue(field, v))}
          precision={Boolean(field.precision)}
          {...(error ? { error } : {})}
          {...(field.help ? { help: field.help } : {})}
          disabled={disabled}
        />
      )
    case 'url':
      return <UrlField {...props} />
    case 'money':
      return <MoneyField {...props} />
    case 'taglist':
      return <TagListField {...props} />
    case 'partylist':
      return <PartyListField {...props} />
    case 'percent':
    case 'number':
    case 'year':
      return (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>{label}</span>
          <TextInput
            size="large"
            ariaLabel={field.label}
            value={value === null || value === undefined ? '' : String(value)}
            onChange={(v) => onChange(v === '' ? undefined : Number(v.replace(/[^0-9.-]/g, '')))}
            placeholder={field.input === 'percent' ? '0 to 100' : undefined}
            {...(error ? { error } : {})}
            disabled={disabled}
          />
        </div>
      )
    default:
      return (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>{label}</span>
          <TextInput
            size="large"
            ariaLabel={field.label}
            inputType={field.input === 'email' ? 'email' : 'text'}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
            {...(error ? { error } : {})}
            disabled={disabled}
          />
        </div>
      )
  }
}
